import React, { createContext, useEffect, useState } from "react";
import ICookiePreferences from "./ICookiePreferences";
import {
    getCookiePreferences,
    setCookiePreferences,
    removeCookiePreferences,
} from "./CookiePreferenceStorageService";

interface IProps {
    children: React.ReactNode;
    onPreferencesUpdate: (preferences: ICookiePreferences) => void;
}

export interface ICookieContext {
    cookieBannerIsOpen: boolean;

    preferencesDialogIsOpen: boolean;
    openPreferencesDialog: () => void;
    closePreferencesDialog: () => void;

    preferences?: ICookiePreferences;
    savePreferences: (preferences: ICookiePreferences) => void;
}

const defaultCookieContext: ICookieContext = {
    cookieBannerIsOpen: false,

    preferencesDialogIsOpen: false,
    openPreferencesDialog: () => {},
    closePreferencesDialog: () => {},

    preferences: undefined,
    savePreferences: () => {},
};

export const CookieContext = createContext<ICookieContext>(defaultCookieContext);

export const CookieContextProvider = (props: IProps) => {
    const { onPreferencesUpdate, children } = props;

    const localStoragePreferences = getCookiePreferences();

    const [cookieBannerIsOpen, setCookieBannerIsOpen] = useState<boolean>(
        typeof localStoragePreferences === "undefined"
    );

    const [preferencesDialogIsOpen, setPreferencesDialogIsOpen] = useState<boolean>(
        defaultCookieContext.preferencesDialogIsOpen
    );

    const [preferences, setPreferences] = useState<ICookiePreferences | undefined>(
        localStoragePreferences
    );

    useEffect(() => {
        if (typeof preferences === "undefined") return;

        onPreferencesUpdate(preferences);
    }, []);

    const savePreferences = (_preferences: ICookiePreferences) => {
        setPreferences(_preferences);
        onPreferencesUpdate(_preferences);

        if (_preferences.prefenceCookies.isEnabled) {
            setCookiePreferences(_preferences);
        } else {
            removeCookiePreferences();
        }

        setPreferencesDialogIsOpen(false);
        setCookieBannerIsOpen(false);
    };

    const openPreferencesDialog = () => {
        closeCookieBanner();
        setPreferencesDialogIsOpen(true);
    };

    const closePreferencesDialog = () => {
        setPreferencesDialogIsOpen(false);

        if (typeof preferences === "undefined") openCookieBanner();
    };

    const openCookieBanner = () => {
        setCookieBannerIsOpen(true);
    };

    const closeCookieBanner = () => {
        setCookieBannerIsOpen(false);
    };

    const cookieStore: ICookieContext = {
        cookieBannerIsOpen: cookieBannerIsOpen,

        preferencesDialogIsOpen: preferencesDialogIsOpen,
        openPreferencesDialog: openPreferencesDialog,
        closePreferencesDialog: closePreferencesDialog,

        preferences: preferences,
        savePreferences: (preferences: ICookiePreferences) => savePreferences(preferences),
    };

    return <CookieContext.Provider value={cookieStore}> {children} </CookieContext.Provider>;
};
